import * as Yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";
import { isValidValue } from "./util/generalUtil";

// Helper function to parse numbers safely
const parseNumber = (value) => {
  if (typeof value === "string") {
    return parseFloat(value.replace(/,/g, "")); // Remove commas before parsing
  }
  return typeof value === "number" ? value : NaN;
};

Yup.addMethod(Yup.string, "appendCountryCode", function (countryCode) {
  return this.transform((value) =>
    value?.startsWith("+") ? value : `${countryCode}${value?.slice(1)}`
  );
});

Yup.addMethod(Yup.string, "phonenumber", function (errorMessage) {
  return this.test(`test-phone-type`, errorMessage, function (value) {
    const { path, createError } = this;

    if (!value) return true;

    return (
      isValidPhoneNumber(value ?? "") ||
      createError({ path, message: errorMessage })
    );
  });
});

Yup.addMethod(Yup.string, "emailOrPhone", function (errorMessage) {
  return this.test(
    `test-email-or-phone-type`,
    errorMessage,
    async function (value) {
      const { path, createError } = this;

      let validEmail = true;

      try {
        validEmail = await Yup.string().email().validate(value);
      } catch (e) {
        validEmail = false;
      }

      const phone = Yup.string()
        .appendCountryCode("+234")
        .cast(value ?? "");

      return (
        validEmail ||
        isValidPhoneNumber(phone) ||
        createError({ path, message: errorMessage })
      );
    }
  );
});

Yup.addMethod(Yup.string, "emailAddress", function (errorMessage) {
  return this.test(`test-email-address`, errorMessage, async function (value) {
    const { path, createError } = this;

    let validEmail = true;

    try {
      validEmail =
        value?.length > 0
          ? await Yup.string().email().validate(value)
          : validEmail;
    } catch (e) {
      validEmail = false;
    }

    return validEmail || createError({ path, message: errorMessage });
  });
});

const isValidNumber = (value) => {
  value ??= "";
  const valueTrimmed = value?.trim();
  return (
    valueTrimmed == "" || !isNaN(parseFloat(valueTrimmed?.replace(/\,/g, "")))
  );
};

Yup.addMethod(Yup.string, "numberFormat", function (args, message) {
  const { min, max } = args;

  return this.test(`test-number-format`, message, async function (value) {
    const { path, createError } = this;

    let newVal = parseFloat(value?.replace(/\,/g, ""));

    let isValid = isValidNumber(value);

    if (min) {
      if (newVal < min) {
        message = `${newVal} must be greater than ${min}`;
        isValid = false;
      }
    }

    if (max) {
      if (newVal > max) {
        message = `${newVal} must be less than ${max}`;
        isValid = false;
      }
    }

    return isValid || createError({ path, message });
  });
});

Yup.addMethod(Yup.string, "equals", function (args, message) {
  return this.test(`test-string-equals`, message, function (value) {
    const { path, createError, options } = this;
    const fieldName = args.key;

    if (!fieldName) {
      throw new Error("Field name argument is missing.");
    }

    const fieldValue = parseNumber(options.parent[fieldName]);
    const newVal = parseNumber(value);

    if (isNaN(newVal) || isNaN(fieldValue)) {
      return createError({ path, message: "Invalid number format." });
    }

    if (newVal !== fieldValue) {
      return createError({
        path,
        message: message || `Value must be equal to ${fieldValue}`,
      });
    }

    return true;
  });
});

Yup.addMethod(Yup.string, "lessThanOrEqual", function (args, message) {
  return this.test(`test-number-lessThanOrEqual`, message, function (value) {
    const { path, createError, options } = this;
    const fieldName = args.key;

    if (!fieldName) {
      throw new Error("Field name argument is missing.");
    }

    // Get the comparison field value from the form
    let fieldValue = options.parent[fieldName];

    // Convert both values to numbers
    const parsedValue = value ? parseFloat(value.replace(/,/g, "")) : NaN;
    const parsedFieldValue =
      typeof fieldValue === "string"
        ? parseFloat(fieldValue.replace(/,/g, ""))
        : fieldValue;

    // Ensure both values are valid numbers
    if (isNaN(parsedValue) || isNaN(parsedFieldValue)) {
      return createError({ path, message: "Invalid number format." });
    }

    // Perform the validation
    if (parsedValue > parsedFieldValue) {
      return createError({
        path,
        message:
          message || `Value must be less than or equal to ${parsedFieldValue}`,
      });
    }

    return true;
  });
});

// Less Than
Yup.addMethod(Yup.string, "lessThan", function (args, message) {
  return this.test(`test-number-lessThan`, message, function (value) {
    const { path, createError, options } = this;
    const fieldName = args.key;

    if (!fieldName) {
      throw new Error("Field name argument is missing.");
    }

    const fieldValue = parseNumber(options.parent[fieldName]);
    const newVal = parseNumber(value);

    if (isNaN(newVal) || isNaN(fieldValue)) {
      return createError({ path, message: "Invalid number format." });
    }

    if (newVal >= fieldValue) {
      return createError({
        path,
        message: message || `Value must be less than ${fieldValue}`,
      });
    }

    return true;
  });
});

// Less Than or Equal
Yup.addMethod(Yup.string, "lessThanOrEqual", function (args, message) {
  return this.test(`test-number-lessThanOrEqual`, message, function (value) {
    const { path, createError, options } = this;
    const fieldName = args.key;

    if (!fieldName) {
      throw new Error("Field name argument is missing.");
    }

    const fieldValue = parseNumber(options.parent[fieldName]);
    const newVal = parseNumber(value);

    if (isNaN(newVal) || isNaN(fieldValue)) {
      return createError({ path, message: "Invalid number format." });
    }

    if (newVal > fieldValue) {
      return createError({
        path,
        message: message || `Value must be less than or equal to ${fieldValue}`,
      });
    }

    return true;
  });
});

// Greater Than or Equal
Yup.addMethod(Yup.string, "greaterThanOrEqual", function (args, message) {
  return this.test(`test-number-greaterThanOrEqual`, message, function (value) {
    const { path, createError, options } = this;
    const fieldName = args.key;

    if (!fieldName) {
      throw new Error("Field name argument is missing.");
    }

    const fieldValue = parseNumber(options.parent[fieldName]);
    const newVal = parseNumber(value);

    if (isNaN(newVal) || isNaN(fieldValue)) {
      return createError({ path, message: "Invalid number format." });
    }

    if (newVal < fieldValue) {
      return createError({
        path,
        message:
          message || `Value must be greater than or equal to ${fieldValue}`,
      });
    }

    return true;
  });
});

// Greater Than
Yup.addMethod(Yup.string, "greaterThan", function (args, message) {
  return this.test(`test-number-greaterThan`, message, function (value) {
    const { path, createError, options } = this;
    const fieldName = args.key;

    if (!fieldName) {
      throw new Error("Field name argument is missing.");
    }

    const fieldValue = parseNumber(options.parent[fieldName]);
    const newVal = parseNumber(value);

    if (isNaN(newVal) || isNaN(fieldValue)) {
      return createError({ path, message: "Invalid number format." });
    }

    if (newVal <= fieldValue) {
      return createError({
        path,
        message: message || `Value must be greater than ${fieldValue}`,
      });
    }

    return true;
  });
});

// inside your formValidation file (replace existing multiNumberValidation)
Yup.addMethod(Yup.string, "multiNumberValidation", function (args = {}) {
  return this.test(
    "multi-number-validation",
    "Invalid input",
    function (value) {
      const { path, createError } = this;

      // treat null/undefined/empty string as empty (let required() or caller decide)
      if (!value && value !== "") return true;

      // normalize args
      const digits =
        args?.digits !== undefined ? Number(args.digits) : undefined;
      const min = args?.min !== undefined ? Number(args.min) : undefined;
      const max = args?.max !== undefined ? Number(args.max) : undefined;
      const repeat = (args?.repeat ?? "yes").toString().toLowerCase();
      const required = !!args?.required;

      // choose delimiter detection: prefer explicit delimiter in args if provided,
      // otherwise inspect the value for comma/dash — fallback to "none".
      let delimiterType = args?.delimiter || null;
      if (!delimiterType) {
        if (String(value).includes(",")) delimiterType = "comma";
        else if (String(value).includes("-")) delimiterType = "dash";
        else delimiterType = "none";
      }

      let parts = [];
      if (delimiterType === "comma") {
        parts = String(value)
          .split(",")
          .map((p) => (p ?? "").trim());
      } else if (delimiterType === "dash") {
        parts = String(value)
          .split("-")
          .map((p) => (p ?? "").trim());
      } else {
        // none: treat input as a sequence of characters/digits
        // each character is considered a separate "box"
        parts = String(value)
          .split("")
          .map((p) => (p ?? "").trim());
      }

      // Required: all boxes must be filled (only if required flag set)
      if (required) {
        const hasEmpty = parts.some((x) => x === "");
        if (hasEmpty) {
          return createError({ path, message: "All boxes must be filled" });
        }
      }

      // If digits is defined, verify the number of parts exactly equals digits
      if (typeof digits === "number") {
        if (parts.length !== digits) {
          return createError({
            path,
            message: `Exactly ${digits} values are required`,
          });
        }
      }

      // Only digits
      if (!parts.every((p) => p === "" || /^\d+$/.test(p))) {
        return createError({ path, message: "Only digits are allowed" });
      }

      // Min / Max checks for each non-empty part
      for (const p of parts) {
        if (p === "") continue;
        const n = Number(p);
        if (!Number.isFinite(n)) {
          return createError({ path, message: "Only digits are allowed" });
        }
        if (min !== undefined && n < min) {
          return createError({
            path,
            message: `Each value must be ≥ ${min}`,
          });
        }
        if (max !== undefined && n > max) {
          return createError({
            path,
            message: `Each value must be ≤ ${max}`,
          });
        }
      }

      // Uniqueness if repeat === "no"
      if (repeat === "no") {
        const normalized = parts.filter((p) => p !== "");
        const set = new Set(normalized);
        if (set.size !== normalized.length) {
          return createError({ path, message: "Values must be unique" });
        }
      }

      return true;
    }
  );
});

//   console.log("email validated", Yup.string().emailOrPhone().validate("07014385663").then(res => console.log("result", res)))

export const validationTypes = (item) => {
  let { type, mapType } = item;
  let validation = item?.validation || {};
  const MIN_DATE = "1900-1-1";
  switch (type) {
    case "text":
      return Yup.string().nullable();
    case "email":
      return Yup.string().emailAddress("not a valid email").nullable();
    case "date":
      return Yup.date()
        .nullable()
        .min(validation?.min ?? MIN_DATE);
    case "datetime":
      return Yup.date()
        .nullable()
        .min(validation?.min ?? MIN_DATE);
    case "number":
      return Yup.string().numberFormat(validation, "not a valid number");
    case "amount":
      return Yup.string().numberFormat(validation, "not a valid number");
    case "checkbox":
      return Yup.array();
    case "multinumber":
      return Yup.string().multiNumberValidation(validation);
    case "radio":
      return Yup.string().nullable();
    case "multiselect":
    case "upload":
      return Yup.array();
    case "select":
      return mapType === "mapSelectToString"
        ? Yup.string().nullable()
        : Yup.object().nullable();
    case "phonenumber":
      return Yup.string().phonenumber("not a valid phonenumber").nullable();
    default:
      return Yup.string();
  }
};

function getInputName(name) {
  return name.replace(/_line_\d/, "").replace(/_/g, " ");
}

export const mapValidationSchema = (item, formikStatus = {}) => {
  //if(!item.validation || !item.type) return null;

  let { type, name } = item;

  if (!type) return null;

  let validation = null;

  if (type === "number" && formikStatus) {
    console.log("mapValidationSchema", formikStatus);
  }

  let formikStatusValidation = formikStatus[name]?.validation;
  validation = formikStatusValidation
    ? validationTypes({ ...item, validation: formikStatusValidation })
    : validationTypes(item);

  console.log("mapValidationSchema item", name, item, validation);

  if (type == "number") {
    const {
      greaterThan,
      greaterThanOrEqual,
      lessThan,
      lessThanOrEqual,
      equals,
    } = item.validation || {};

    console.log(
      "lets see if this is correct",
      isValidValue(lessThanOrEqual),
      name,
      lessThanOrEqual
    );
    if (isValidValue(lessThanOrEqual)) {
      validation = validation.lessThanOrEqual(
        Yup.ref(lessThanOrEqual),
        `${getInputName(name)} must be less than or equal to ${getInputName(
          lessThanOrEqual
        )}`
      );
    }

    if (isValidValue(equals)) {
      validation = validation.equals(
        Yup.ref(equals),
        `${getInputName(name)} must be equal to ${getInputName(equals)}`
      );
    }

    if (isValidValue(lessThan)) {
      validation = validation.lessThan(
        Yup.ref(lessThan),
        `${getInputName(name)} must be less than ${getInputName(lessThan)}`
      );
    }

    if (isValidValue(greaterThanOrEqual)) {
      validation = validation.greaterThanOrEqual(
        Yup.ref(greaterThanOrEqual),
        `${getInputName(name)} must be greater than or equal to ${getInputName(
          greaterThanOrEqual
        )}`
      );
    }

    if (isValidValue(greaterThan)) {
      validation = validation.greaterThan(
        Yup.ref(greaterThan),
        `${getInputName(name)} must be greater than ${getInputName(
          greaterThan
        )}`
      );
    }
  }

  //map required
  if (formikStatusValidation?.required ?? item.validation?.required) {
    validation = validation?.required("This field is required");
    // only apply min(1) to array-like fields:
    if (["multiselect", "upload", "checkbox"].includes(type)) {
      validation = validation?.min(1, "select at least one item");
    }
  }
  //map default values
  if (item.validation?.default) {
    validation = validation?.default(item.validation?.default);
  }

  return validation;
};